import React from 'react';
import { Lightbulb, Calculator, Home, Sliders } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров освещения
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  roomTypes,
  lampTypes
}) => {
  // Парсинг числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Расчет текущей площади помещения
  const roomArea = parseFloatOrDefault(formData.roomLength, 0) * parseFloatOrDefault(formData.roomWidth, 0);
  
  // Обработчик изменения типа помещения
  const handleRoomTypeChange = (roomTypeId) => {
    const selectedRoomType = roomTypes.find(type => type.id === roomTypeId);
    
    setFormData(prev => ({
      ...prev,
      roomType: roomTypeId,
      targetIlluminance: selectedRoomType ? selectedRoomType.defaultIlluminance.toString() : prev.targetIlluminance
    }));
  };
  
  // Обработчик изменения типа лампы
  const handleLampTypeChange = (lampTypeId) => {
    const selectedLampType = lampTypes.find(type => type.id === lampTypeId);
    
    setFormData(prev => ({
      ...prev,
      lampType: lampTypeId,
      lampEfficiency: selectedLampType ? selectedLampType.defaultEfficiency.toString() : prev.lampEfficiency,
      lampPower: selectedLampType ? selectedLampType.defaultLampPower.toString() : prev.lampPower,
      lampFlux: selectedLampType ? selectedLampType.defaultFlux.toString() : prev.lampFlux,
      customLampEnabled: lampTypeId === 'custom'
    }));
  };
  
  // Обработчик изменения требуемой освещенности
  const handleIlluminanceChange = (e) => {
    setFormData(prev => ({
      ...prev,
      targetIlluminance: e.target.value
    }));
  };
  
  // Обработчик изменения размеров помещения
  const handleRoomSizeChange = (dimension, value) => {
    setFormData(prev => ({
      ...prev,
      [dimension]: value
    }));
  };
  
  // Функция расчета
  const calculateLighting = () => {
    // Вызов основной функции расчета через нажатие кнопки отправки формы
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные типы освещенности
  const illuminanceLevels = [
    { value: '150', label: '150 лк', description: 'Минимальное' },
    { value: '300', label: '300 лк', description: 'Стандартное' },
    { value: '500', label: '500 лк', description: 'Офисное' },
    { value: '750', label: '750 лк', description: 'Яркое' }
  ];
  
  // Предопределенные размеры помещений
  const roomSizes = [
    { label: 'Малая комната', length: '3', width: '3' },
    { label: 'Стандартная комната', length: '4', width: '5' },
    { label: 'Большая комната', length: '6', width: '5' },
    { label: 'Офисное пространство', length: '8', width: '6' }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-amber-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Тип помещения */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Тип помещения</div>
          <div className="grid grid-cols-2 gap-2">
            {roomTypes.slice(0, 6).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleRoomTypeChange(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors text-left
                  ${formData.roomType === type.id 
                    ? 'bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium truncate">{type.name}</div>
                <div className="text-xs opacity-75">{type.defaultIlluminance} лк</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип светильника */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Тип источника света</div>
          <div className="grid grid-cols-2 gap-2">
            {lampTypes.slice(0, 4).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => handleLampTypeChange(type.id)}
                className={`p-2 text-sm rounded-lg border transition-colors text-left
                  ${formData.lampType === type.id 
                    ? 'bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium truncate">{type.name}</div>
                <div className="text-xs opacity-75">{type.defaultEfficiency} лм/Вт</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Требуемая освещенность */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="illuminanceSlider" className="text-gray-600 dark:text-gray-400">Требуемая освещенность</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.targetIlluminance} лк</span>
          </div>
          <input
            type="range"
            id="illuminanceSlider"
            min="100"
            max="1000"
            step="50"
            value={formData.targetIlluminance}
            onChange={handleIlluminanceChange}
            aria-label="Требуемая освещенность"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-600"
          />
          
          {/* Кнопки с предопределенными уровнями освещенности */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {illuminanceLevels.map(level => (
              <button
                key={level.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, targetIlluminance: level.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.targetIlluminance === level.value 
                    ? 'bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                title={level.description}
              >
                {level.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Информация о помещении */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 p-3">
          <div className="flex items-start">
            <Home className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Параметры помещения</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-amber-600 dark:text-amber-400">
                <div className="flex justify-between">
                  <span>Длина:</span>
                  <span className="font-medium">{formData.roomLength} м</span>
                </div>
                <div className="flex justify-between">
                  <span>Ширина:</span>
                  <span className="font-medium">{formData.roomWidth} м</span>
                </div>
                <div className="flex justify-between">
                  <span>Высота:</span>
                  <span className="font-medium">{formData.roomHeight} м</span>
                </div>
                <div className="flex justify-between">
                  <span>Площадь:</span>
                  <span className="font-medium">{formatNumber(roomArea, 1)} м²</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Предопределенные размеры помещений */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Шаблоны размеров</div>
          <div className="grid grid-cols-2 gap-2">
            {roomSizes.map((size, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    roomLength: size.length,
                    roomWidth: size.width
                  }));
                }}
                className="p-2 text-xs rounded-lg border transition-colors bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                <div className="font-medium">{size.label}</div>
                <div className="opacity-75">{size.length} × {size.width} м</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateLighting}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать освещение
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;